/**
 * Whether the tour is running, and whether it was told to stop for good.
 *
 * Two different lifetimes, so two different stores. Running is a property of
 * this page load and lives in memory; "Daugiau neberodyti" is a property of the
 * account on this device and lives in Dexie, next to the theme and the profile
 * cache — which is also what makes it clear when the phone changes hands.
 *
 * The stored value is a version rather than a flag. A tour that gains a step
 * worth seeing can raise `TOUR_VERSION` and be offered once more; a boolean
 * could only ever be reset by asking somebody to go and find a setting.
 */

import { create } from 'zustand'
import { getMeta, setMeta } from '../db/dexie.js'

/** Raise to re-offer the tour to somebody who has already dismissed it. */
export const TOUR_VERSION = 1

export const TOUR_META_KEY = 'tour.dismissedVersion'

export async function tourDismissed(): Promise<boolean> {
  const seen = await getMeta<unknown>(TOUR_META_KEY, 0)
  return typeof seen === 'number' && seen >= TOUR_VERSION
}

/** „Daugiau neberodyti“ — for this account, on this device, until the next version. */
export async function stopShowingTour(): Promise<void> {
  await setMeta(TOUR_META_KEY, TOUR_VERSION)
}

interface TourState {
  open: boolean
  /** Bumped on every start so a replay remounts rather than resuming mid-step. */
  run: number
}

export const useTourState = create<TourState>(() => ({ open: false, run: 0 }))

export function startTour(): void {
  useTourState.setState((state) => ({ open: true, run: state.run + 1 }))
}

export function endTour(): void {
  useTourState.setState({ open: false })
}
